/**
 * Optimistic "signing in progress" state for the embedded ceremony (GEN-5259).
 *
 * When a clinician clicks "Sign TRF" they leave the order page for a full-page
 * `/sign` load, complete the DocuSign ceremony, and return to the order page by
 * another full-page load. On return the order still projects the pre-sign status
 * (`sent`/`delivered`) until Connect confirms `signed` and the poller reads it, so
 * without help the page looks unsigned: the Sign button stays clickable and the
 * badge still says "TRF opened by signer".
 *
 * This hook bridges that window with an optimistic cue. Because the Sign click is
 * a hard navigation, in-memory React state cannot carry the intent across it, so
 * the intent is recorded in `sessionStorage` keyed by order. On the order page the
 * hook reads that intent and, while its budget has not elapsed and the signature
 * has not reached a terminal status, reports `signingInProgress` so the page can
 * show a spinner and disable the Sign button.
 *
 * The budget is anchored to the *return*, not to the click: it bounds how long the
 * cue is on screen, and the ceremony itself is off-screen. Anchoring it to the
 * click would spend the budget on minting, loading the iframe, reading and signing
 * — routinely longer than the budget itself — and a clinician coming back from a
 * normal-length ceremony would land on exactly the state this fixes. The click
 * timestamp is kept only to age out an intent whose ceremony was abandoned long
 * ago (see {@link TRF_SIGNING_INTENT_MAX_AGE_MS}).
 *
 * It resolves in exactly two ways, matching the poller that is the source of truth:
 *
 *   1. **The event lands.** The existing signature poll flips `trfSignature` to a
 *      terminal status (`signed`, `declined`, `canceled`, `expired`). The hook
 *      clears the intent and drops the cue immediately, and the real badge takes
 *      over — no second Sign click.
 *   2. **The budget elapses.** The clinician did not complete the ceremony (closed
 *      the window, abandoned it) and no event arrived within the budget. The hook
 *      clears the intent and drops the cue, so the page falls back to the real,
 *      still-signable status ("TRF opened by signer") and the clinician can click
 *      "Sign TRF" to try again.
 *
 * Kept as a standalone hook (not inline in the ~4,600-LOC `OrderInProgress.tsx`)
 * so the page file does not grow another effect and the lifecycle is unit-testable.
 */

import { useEffect, useState } from 'react'

import { TRF_SIGNATURE_POLL_MAX_DURATION_MS } from '@/pages/order/useTrfSignaturePolling'
import type { OrderTrfSignature } from '@/utils/orderTrfSignature'

/**
 * How long the optimistic cue stays on screen before giving up and reverting to
 * the real status. Measured from the moment the order page first sees the intent —
 * the clinician is back from the ceremony — so it bounds only the visible wait for
 * Connect to confirm, not the ceremony that preceded it.
 *
 * Thirty seconds is comfortably longer than the confirmation normally takes (the
 * poll reads every five seconds) and short enough that an abandoned ceremony does
 * not leave the clinician looking at a disabled Sign button.
 */
export const TRF_SIGNING_IN_PROGRESS_MAX_MS = 30 * 1000

/**
 * How old the Sign click may be for the intent to still be worth honoring.
 *
 * This is not the display budget — it only ages out an intent whose ceremony was
 * clearly abandoned, so revisiting the order much later in the same tab does not
 * greet the clinician with a spinner. Set to the signature poll's own cap: past
 * that horizon a single page's poll session has already given up, so a cue raised
 * then has nothing left that could resolve it.
 */
export const TRF_SIGNING_INTENT_MAX_AGE_MS = TRF_SIGNATURE_POLL_MAX_DURATION_MS

const STORAGE_PREFIX = 'trf-signing-in-progress:'

const storageKey = (orderId: string): string => `${STORAGE_PREFIX}${orderId}`

/** A signing ceremony the clinician started for an order. */
export interface TrfSigningIntent {
  /** When the clinician clicked "Sign TRF" (epoch ms). */
  startedAt: number
  /**
   * When the cue must stop showing (epoch ms), or null while the ceremony is still
   * off-screen. Stamped the first time the order page sees the intent, which is
   * what anchors the display budget to the return rather than the click; persisting
   * it keeps one budget across a reload instead of restarting it on every mount.
   */
  deadline: number | null
}

/**
 * `sessionStorage`, or null when it is unavailable (SSR, or a privacy mode that
 * throws on access). A missing store simply means no optimistic cue — the page
 * still resolves correctly off the poller, just without the interim spinner.
 */
function getStore(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

/** Persist `intent` for `orderId` (best-effort). */
function writeTrfSigningIntent(orderId: string, intent: TrfSigningIntent): void {
  const store = getStore()
  if (!store || !orderId) return
  try {
    store.setItem(storageKey(orderId), JSON.stringify(intent))
  } catch {
    // No store write means no cue; the poller still resolves the status.
  }
}

/**
 * Record that a signing ceremony was started for `orderId`, so the order page can
 * show the optimistic cue when the clinician returns. The deadline is left unset:
 * the budget starts when the page next reads this intent, not now.
 */
export function markTrfSigningInProgress(orderId: string, now: number = Date.now()): void {
  writeTrfSigningIntent(orderId, { startedAt: now, deadline: null })
}

/** Clear the signing intent for `orderId` (best-effort). */
export function clearTrfSigningInProgress(orderId: string): void {
  const store = getStore()
  if (!store || !orderId) return
  try {
    store.removeItem(storageKey(orderId))
  } catch {
    // Nothing actionable; a stale intent is aged out on the next read.
  }
}

/**
 * The stored signing intent for `orderId`, or null when none is stored or it is
 * unreadable. An unparseable or malformed entry reads as null, so a corrupt value
 * costs the cue rather than arming it on nonsense.
 */
export function readTrfSigningIntent(orderId: string): TrfSigningIntent | null {
  const store = getStore()
  if (!store || !orderId) return null
  try {
    const raw = store.getItem(storageKey(orderId))
    if (raw == null) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { startedAt, deadline } = parsed as Partial<TrfSigningIntent>
    if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) return null
    const hasDeadline = typeof deadline === 'number' && Number.isFinite(deadline)
    return { startedAt, deadline: hasDeadline ? deadline : null }
  } catch {
    return null
  }
}

interface UseTrfSigningInProgressParams {
  /** Order whose signing state to track; the cue is off until this is known. */
  orderId: string | null | undefined
  /**
   * The order's current signature status as the page holds it. A terminal status
   * (the poller having read `signed`/`declined`/etc.) resolves the cue at once.
   */
  signature: OrderTrfSignature | null
  /** Test seam: override the display budget. */
  maxMs?: number
  /** Test seam: override how old a Sign click may be to still be honored. */
  maxIntentAgeMs?: number
}

export interface UseTrfSigningInProgressResult {
  /**
   * Whether to show the optimistic "checking your signature status" cue:
   * hide/disable the Sign button and show a spinner in place of the badge.
   */
  signingInProgress: boolean
}

/**
 * Reports whether the order is in its optimistic signing window — an intent was
 * left by the Sign click, its display budget has not elapsed, and no terminal
 * status has arrived yet.
 *
 * The intent is read once per order (the arm effect keys on `orderId`, not on the
 * signature) so a burst of non-terminal poll reads does not restart the budget
 * timer. A separate effect watches the signature and ends the cue the instant a
 * terminal status lands.
 */
export function useTrfSigningInProgress({
  orderId,
  signature,
  maxMs = TRF_SIGNING_IN_PROGRESS_MAX_MS,
  maxIntentAgeMs = TRF_SIGNING_INTENT_MAX_AGE_MS,
}: UseTrfSigningInProgressParams): UseTrfSigningInProgressResult {
  const [signingInProgress, setSigningInProgress] = useState(false)

  // Arm the cue from the stored intent and hold it for the remaining budget.
  useEffect(() => {
    if (!orderId) {
      setSigningInProgress(false)
      return
    }
    const intent = readTrfSigningIntent(orderId)
    if (intent == null) {
      setSigningInProgress(false)
      return
    }
    const now = Date.now()
    // The ceremony was abandoned long enough ago that nothing is coming; drop the
    // intent rather than greet a later visit with a spinner.
    if (now - intent.startedAt >= maxIntentAgeMs) {
      clearTrfSigningInProgress(orderId)
      setSigningInProgress(false)
      return
    }
    // First sighting on the order page means the clinician is back from the
    // ceremony, so the budget starts here. A deadline already stamped by an
    // earlier mount is reused, so a reload continues that budget instead of
    // granting a fresh one.
    const deadline = intent.deadline ?? now + maxMs
    if (intent.deadline == null) {
      writeTrfSigningIntent(orderId, { ...intent, deadline })
    }
    const remaining = deadline - now
    if (remaining <= 0) {
      clearTrfSigningInProgress(orderId)
      setSigningInProgress(false)
      return
    }
    setSigningInProgress(true)
    const timer = setTimeout(() => {
      clearTrfSigningInProgress(orderId)
      setSigningInProgress(false)
    }, remaining)
    return () => clearTimeout(timer)
  }, [orderId, maxMs, maxIntentAgeMs])

  // The event landed: the poller read a terminal status, so resolve the cue at
  // once and let the real badge take over. Non-terminal transitions
  // (`sent`->`delivered`) are still "in progress" and deliberately do not clear.
  useEffect(() => {
    if (!orderId || !signingInProgress) return
    if (signature && (signature.isSigned || signature.isTerminal)) {
      clearTrfSigningInProgress(orderId)
      setSigningInProgress(false)
    }
  }, [orderId, signingInProgress, signature])

  return { signingInProgress }
}
